import { alpha, Box, Chip, Stack } from '@mui/material'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { com } from '@eidu/web'
import { ReactElement } from 'react'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import { findEntityTypeByFieldId, findFieldById } from '../../util/EntityTypeExtensions'
import fieldValueLabel from '../../util/FieldValueExtensions'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityId = com.eidu.sharedlib.entity.EntityId
import EntitySelector = com.eidu.sharedlib.entity.selector.EntitySelector

type EntityTypesMap = ReadonlyMap<EntityTypeId, EntityType>
type EntitiesByTypeId = ReadonlyMap<EntityTypeId, ReadonlyMap<EntityId, EntityWithLabelAndRelated>>

const Combinator = ({ label }: { label: string }) => (
  <Box
    component="span"
    sx={{
      px: 0.75,
      py: 0.125,
      borderRadius: 0.5,
      bgcolor: 'action.hover',
      color: 'text.secondary',
      fontSize: '0.7rem',
      fontWeight: 700,
      letterSpacing: 0.5,
      lineHeight: 1.6,
      alignSelf: 'center',
    }}
  >
    {label}
  </Box>
)

const TypeChip = ({ name, keyPrefix }: { name: string; keyPrefix: string }) => (
  <Chip
    key={keyPrefix}
    icon={<PeopleAltOutlinedIcon />}
    label={name}
    color="primary"
    variant="filled"
    sx={(theme) => ({
      bgcolor: alpha(theme.palette.primary.main, 0.1),
      color: theme.palette.primary.dark,
      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
      fontWeight: 600,
      '& .MuiChip-icon': { color: theme.palette.primary.dark },
    })}
  />
)

const FilterChip = ({
  icon,
  fieldLabel,
  value,
  keyPrefix,
}: {
  icon: ReactElement
  fieldLabel: string
  value: ReactElement | string
  keyPrefix: string
}) => (
  <Chip
    key={keyPrefix}
    icon={icon}
    label={
      <span>
        {fieldLabel}
        <Box component="span" sx={{ mx: 0.5, color: 'text.secondary' }}>
          =
        </Box>
        <strong>{value}</strong>
      </span>
    }
    variant="outlined"
  />
)

const renderFilterLeaf = (
  selector: EntitySelector,
  entityTypes: EntityTypesMap,
  entitiesByTypeId: EntitiesByTypeId,
  key: string
): ReactElement | null => {
  const types = [...entityTypes.values()]
  if (selector instanceof EntitySelector.FieldValueEquals) {
    const entityType = findEntityTypeByFieldId(types, selector.fieldId)
    const field = entityType ? findFieldById(entityType, selector.fieldId) : undefined
    const fieldLabel = field?.name ?? 'unknown field'
    const value = field ? fieldValueLabel(field, selector.value, entitiesByTypeId) ?? '(loading…)' : '(loading…)'
    return (
      <FilterChip key={key} keyPrefix={key} icon={<FilterAltOutlinedIcon />} fieldLabel={fieldLabel} value={value} />
    )
  }
  if (selector instanceof EntitySelector.FieldValueEqualsUserEntityId) {
    const entityType = findEntityTypeByFieldId(types, selector.fieldId)
    const field = entityType ? findFieldById(entityType, selector.fieldId) : undefined
    const fieldLabel = field?.name ?? 'unknown field'
    return (
      <FilterChip key={key} keyPrefix={key} icon={<PersonOutlineIcon />} fieldLabel={fieldLabel} value="current user" />
    )
  }
  if (selector instanceof EntitySelector.FieldValueEqualsUserFieldValue) {
    const entityType = findEntityTypeByFieldId(types, selector.fieldId)
    const userEntityType = findEntityTypeByFieldId(types, selector.userFieldId)
    const field = entityType ? findFieldById(entityType, selector.fieldId) : undefined
    const userField = userEntityType ? findFieldById(userEntityType, selector.userFieldId) : undefined
    const fieldLabel = field?.name ?? 'unknown field'
    const userFieldLabel = userEntityType && userField ? `user.${userEntityType.name}.${userField.name}` : 'user field'
    return (
      <FilterChip
        key={key}
        keyPrefix={key}
        icon={<PersonOutlineIcon />}
        fieldLabel={fieldLabel}
        value={userFieldLabel}
      />
    )
  }
  return null
}

const filterSelectorTypeId = (selector: EntitySelector, entityTypes: EntityTypesMap): EntityTypeId | undefined => {
  const types = [...entityTypes.values()]
  if (
    selector instanceof EntitySelector.FieldValueEquals ||
    selector instanceof EntitySelector.FieldValueEqualsUserEntityId ||
    selector instanceof EntitySelector.FieldValueEqualsUserFieldValue
  )
    return findEntityTypeByFieldId(types, selector.fieldId)?.id
  return undefined
}

const renderRecursive = (
  selector: EntitySelector,
  entityTypes: EntityTypesMap,
  entitiesByTypeId: EntitiesByTypeId,
  key: string
): ReactElement => {
  if (EntitySelector.All.equals(selector)) return <Chip key={key} icon={<PublicOutlinedIcon />} label="All users" />

  if (selector instanceof EntitySelector.OfType) {
    const name = entityTypes.get(selector.typeId)?.name ?? `Unknown type (${selector.typeId})`
    return <TypeChip keyPrefix={key} name={name} />
  }

  const filterChip = renderFilterLeaf(selector, entityTypes, entitiesByTypeId, key)
  if (filterChip) return filterChip

  if (selector instanceof EntitySelector.And || selector instanceof EntitySelector.Or) {
    const combinator = selector instanceof EntitySelector.And ? 'AND' : 'OR'
    const children = [...selector.selectors.asJsReadonlyArrayView()]
    return (
      <Stack key={key} direction="row" flexWrap="wrap" gap={0.5} alignItems="center">
        {children.flatMap((child, index) => {
          const node = renderRecursive(child, entityTypes, entitiesByTypeId, `${key}-${index}`)
          return index === 0 ? [node] : [<Combinator key={`${key}-c-${index}`} label={combinator} />, node]
        })}
      </Stack>
    )
  }

  if (selector instanceof EntitySelector.Not) {
    return (
      <Stack key={key} direction="row" gap={0.5} alignItems="center">
        <Combinator label="NOT" />
        {renderRecursive(selector.selector, entityTypes, entitiesByTypeId, `${key}-n`)}
      </Stack>
    )
  }

  return <Chip key={key} label={selector.toString()} variant="outlined" />
}

const renderSummarized = (
  selector: EntitySelector,
  entityTypes: EntityTypesMap,
  entitiesByTypeId: EntitiesByTypeId,
  direction: 'row' | 'column'
): ReactElement | null => {
  if (EntitySelector.All.equals(selector)) return <Chip icon={<PublicOutlinedIcon />} label="All users" />

  const children = selector instanceof EntitySelector.And ? [...selector.selectors.asJsReadonlyArrayView()] : [selector]

  const typeIds = new Map<string, EntityTypeId>()
  const filterSelectors: EntitySelector[] = []

  for (const child of children) {
    if (child instanceof EntitySelector.OfType) {
      typeIds.set(child.typeId.toString(), child.typeId)
    } else if (
      child instanceof EntitySelector.FieldValueEquals ||
      child instanceof EntitySelector.FieldValueEqualsUserEntityId ||
      child instanceof EntitySelector.FieldValueEqualsUserFieldValue
    ) {
      const inferred = filterSelectorTypeId(child, entityTypes)
      if (inferred) typeIds.set(inferred.toString(), inferred)
      filterSelectors.push(child)
    } else {
      return null
    }
  }

  const typeChips = [...typeIds.values()].map((id, index) => {
    const name = entityTypes.get(id)?.name ?? `Unknown type (${id})`
    return <TypeChip key={`type-${index}`} keyPrefix={`type-${index}`} name={name} />
  })

  const filterChips = filterSelectors.map(
    (child, index) => renderFilterLeaf(child, entityTypes, entitiesByTypeId, `filter-${index}`)!
  )

  if (typeChips.length === 0 && filterChips.length === 0) return null

  if (direction === 'row') {
    return (
      <Stack direction="row" flexWrap="wrap" gap={0.75} alignItems="center">
        {typeChips}
        {filterChips}
      </Stack>
    )
  }

  return (
    <Stack direction="column" gap={0.5} alignItems="flex-start">
      {typeChips.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.75} alignItems="center">
          {typeChips}
        </Stack>
      )}
      {filterChips.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.75} alignItems="center">
          {filterChips}
        </Stack>
      )}
    </Stack>
  )
}

type EntitySelectorChipsProps = {
  selector: EntitySelector
  entityTypes: EntityTypesMap
  entitiesByTypeId: EntitiesByTypeId
  direction?: 'row' | 'column'
}

const EntitySelectorChips = ({
  selector,
  entityTypes,
  entitiesByTypeId,
  direction = 'column',
}: EntitySelectorChipsProps) => {
  const summarized = renderSummarized(selector, entityTypes, entitiesByTypeId, direction)
  return <Box sx={{ py: 1 }}>{summarized ?? renderRecursive(selector, entityTypes, entitiesByTypeId, 'root')}</Box>
}

export default EntitySelectorChips
